import {appFilters} from "@/utils/appFilters";
import {appSorters} from "@/utils/appSorters";
import {filterApplication, getValueFromPropertyPath, searchApplication, sortElements} from "@/utils/helpers";
import {MODAL_TYPE_ERROR} from "@/constants";

const makeActions = ({i18n, detector, router, $delete, $set, settings}) => ({
    showError: (context, event) => {
        context.modalMessage = i18n.tc("error.errorMessage");
        context.modalType = MODAL_TYPE_ERROR;
        context.modalIsShown = true;
    },
    unflagForceLoadApplications: context => (context.forceLoadApplicationsFlag = false),
    unflagForceLoadDevices: context => (context.forceLoadDevicesFlag = false),
    unflagForceLoadProjects: context => (context.forceLoadProjectsFlag = false),
    unflagForceLoadWarehouses: context => (context.forceLoadWarehousesFlag = false),
    redirectAfterLogin: (context, event) =>
        router.push(
            router.currentRoute.query.redirect ? {name: router.currentRoute.query.redirect} : {path: "/"},
            onComplete => {},
            onAbort => {}
        ),

    appendUploadedFileUrl: (context, event) => {
        const {uploaderId, url} = event.data;
        context.uploadedFilesUrls = {...context.uploadedFilesUrls, [uploaderId]: url};
    },

    setStatisticsApplications(context, event) {
        $set(context, "statisticsApplications", event.data);
    },

    logOut: (context, event) => {
        localStorage.removeItem("ARCOLOGY.apiKey");
        context.isAuthenticated = false;
        router.push(
            {name: "login"},
            onComplete => {},
            onAbort => {}
        );
    },
    setLanguage: async (context, event) => {
        await i18n.loadLanguage(event.payload.language);
        await i18n.setLanguage(event.payload.language);
        localStorage.setItem("ARCMAN.locale", event.payload.language);
    },
    setProjects: (context, event) => {
        const projects = settings.dataModels.ENABLED_PROJECTS_TYPES
            ? event.data.projects.filter(item => settings.dataModels.ENABLED_PROJECTS_TYPES.includes(item.type))
            : event.data.projects.filter(item => typeof item.type === "undefined");
        if (projects.length) {
            $set(context, "projects", projects);
        }
    },
    selectProject: (context, event) => {
        if (context.projects.length) {
            let selectedProject = null;
            if (event.data && context.openApplicationsSelectedProjectId) {
                selectedProject = context.projects.find(
                    element => element.id === context.openApplicationsSelectedProjectId
                );
            } else if (event.payload) {
                selectedProject = context.projects.find(element => element.id === event.payload.projectId);
            }
            if (!selectedProject) {
                selectedProject = context.projects[0];
            }
            $set(context, "openApplicationsSelectedProjectId", selectedProject.id);

            // Compute the list of applications compatible with the selected project
            let selectedProjectCapabilitiesSet = new Set();
            if (selectedProject.capabilities) {
                for (let [key, value] of Object.entries(selectedProject.capabilities)) {
                    if (value) {
                        selectedProjectCapabilitiesSet.add(key);
                    }
                }
            }

            context.projectCompatibleApplications = context.applications.filter(item => {
                if (item.versions?.length) {
                    let functionalRequirements = item.versions[item.currentVersionIndex].functionalRequirements;
                    if (
                        functionalRequirements.length === 0 ||
                        functionalRequirements.reduce(
                            (acc, element) => acc && selectedProjectCapabilitiesSet.has(element),
                            true
                        )
                    ) {
                        return item;
                    }
                }
            });

            $set(context, "filteredProjectCompatibleApplications", context.projectCompatibleApplications);
        }
    },

    countFilteredProjectCompatibleApplicationsInSelectedProjectCategories: function(context, event) {
        let selectedProject = context.projects.find(
            project => project.id === context.openApplicationsSelectedProjectId
        );
        // count applications presence in project categories
        const projectApplicationsMap = context.filteredProjectCompatibleApplications.reduce((acc, item) => {
            item.inProjectCategoriesCount = 0;
            acc[item.id] = item;
            return acc;
        }, {});

        for (let category of selectedProject.categories) {
            for (let version of category.versions) {
                let application = projectApplicationsMap[version.applicationId];
                if (application) {
                    application.inProjectCategoriesCount += 1;
                }
            }
        }

        context.filteredProjectCompatibleApplications.forEach((application, index) =>
            $set(context.filteredProjectCompatibleApplications, index, projectApplicationsMap[application.id])
        );
    },

    setApplicationsSortField: (context, event) => {
        $set(context, "applicationsSortField", appSorters[event.payload]);
    },

    sortInventoryApplications: (context, event) => {
        if (context.applicationsSortField.path) {
            sortElements(
                context.applications,
                context.applicationsSortField.order,
                getValueFromPropertyPath.bind(null, context.applicationsSortField.path, i18n.locale)
            );
        }
    },

    sortInventoryFilteredApplications: (context, event) => {
        if (context.applicationsSortField.path) {
            sortElements(
                context.filteredApplications,
                context.applicationsSortField.order,
                getValueFromPropertyPath.bind(null, context.applicationsSortField.path, i18n.locale)
            );
        }
    },

    sortFilteredProjectCompatibleApplications: (context, event) => {
        if (context.applicationsSortField.path) {
            sortElements(
                context.filteredProjectCompatibleApplications,
                context.applicationsSortField.order,
                getValueFromPropertyPath.bind(null, context.applicationsSortField.path, i18n.locale)
            );
        }
    },

    setFilteredInventoryApplications: (context, event) => {
        $set(context, "filteredApplications", context.applications);
    },

    setInventoryApplications: (context, event) => {
        let applications = [];
        if (detector.features.useDefaultWarehouse) {
            applications = event.data.applications.filter(
                item => item.warehouseId === settings.dataModels.DEFAULT_WAREHOUSE_ID
            );
        } else {
            applications = event.data.applications.filter(
                item => item.warehouseId !== settings.dataModels.DEFAULT_WAREHOUSE_ID
            );
        }
        $set(context, "applications", applications);
    },

    setFiltersCriteria: (context, event) => {
        const filtersCriteria = event.payload;
        $set(context, "filtersCriteria", filtersCriteria);
    },

    setSearchCriteria: (context, event) => {
        $set(context, "searchCriteria", event.payload);
    },

    resetFilters: (context, event) => {
        $set(context, "filters", null);
        $set(context, "filtersCriteria", null);
    },

    filterAndSearchInventoryApplications: (context, event) => {
        context.filteredApplications = context.applications
            .filter(filterApplication.bind(null, i18n.locale, context))
            .filter(searchApplication.bind(null, i18n.locale, context.searchCriteria));
    },

    filterAndSearchProjectApplications: function(context, event) {
        context.filteredProjectCompatibleApplications = context.projectCompatibleApplications
            .filter(filterApplication.bind(null, i18n.locale, context))
            .filter(searchApplication.bind(null, i18n.locale, context.searchCriteria));
    },

    initFilters: (context, event) => {
        if (settings.dataModels.APPLICATIONS_ENABLED_FILTERS) {
            let enabledFilters = settings.dataModels.APPLICATIONS_ENABLED_FILTERS.reduce(
                (acc, value) => Object.assign(acc, {[value]: appFilters[value]}),
                {}
            );
            $set(context, "filters", enabledFilters);
        } else {
            $set(context, "filters", appFilters);
        }
    },

    initProjectApplicationsFilters: (context, event) => {
        if (settings.dataModels.PROJECT_APPLICATIONS_ENABLED_FILTERS) {
            let enabledFilters = settings.dataModels.PROJECT_APPLICATIONS_ENABLED_FILTERS.reduce(
                (acc, value) => Object.assign(acc, {[value]: appFilters[value]}),
                {}
            );
            $set(context, "filters", enabledFilters);
        } else {
            $set(context, "filters", appFilters);
        }
    },

    setFiltersOptions: (context, event) => {
        const filtersOptions = event.data;
        for (let key of Object.keys(context.filters)) {
            $set(context.filters[key], "list", filtersOptions[key]);
        }
    },

    updateFilteredApplication(context, event) {
        const {updatedApplication} = event.data;
        const updatedApplicationIndex = context.filteredApplications.findIndex(
            item => item.id === updatedApplication.id
        );
        $set(context.filteredApplications, updatedApplicationIndex, updatedApplication);
    },

    deleteFilteredApplication(context, event) {
        const {deletedApplication} = event.data;
        const deletedApplicationIndex = context.filteredApplications.findIndex(
            item => item.id === deletedApplication.id
        );
        $delete(context.filteredApplications, deletedApplicationIndex, deletedApplication);
    },

    updateProject(context, event) {
        const {project} = event.data;
        const projectIndex = context.projects.findIndex(item => item.id === project.id);
        $set(context.projects, projectIndex, project);
    },
});

export {makeActions};
